import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useProctoring } from '../hooks/useProctoring';
import { RichContent } from '../components/RichEditor';
import { useExamWebSocket } from '../hooks/useExamWebSocket';

// ── Session storage helpers (#7 & #8 — persist exam state across refresh) ────
const SS_KEY = (id) => `exam_state_${id}`;

const saveExamState = (attemptId, state) => {
  try {
    sessionStorage.setItem(SS_KEY(attemptId), JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch { /* silent */ }
};

const loadExamState = (attemptId) => {
  try {
    const raw = sessionStorage.getItem(SS_KEY(attemptId));
    if (!raw) return null;
    const s = JSON.parse(raw);
    // Adjust remaining time for time already elapsed since save
    if (s.remainingSeconds && s.savedAt) {
      const elapsedSinceSave = Math.floor((Date.now() - s.savedAt) / 1000);
      s.remainingSeconds = Math.max(1, s.remainingSeconds - elapsedSinceSave);
    }
    return s;
  } catch { return null; }
};

const clearExamState = (attemptId) => {
  try { sessionStorage.removeItem(SS_KEY(attemptId)); } catch { /* silent */ }
};

// ─── Timer ────────────────────────────────────────────────────────────────────
// DB-driven: initialSeconds comes from server. Counts down locally.
// onTick fires every second with current remaining for heartbeat sync.
function Timer({ initialSeconds, onExpire, onTick, timerKey }) {
  const [display, setDisplay] = useState(initialSeconds);
  const remaining = useRef(initialSeconds);
  const tickRef   = useRef(null);

  // Reset whenever server sends new value (resume, time adjust, reload)
  useEffect(() => {
    remaining.current = initialSeconds;
    setDisplay(initialSeconds);
  }, [timerKey, initialSeconds]);

  useEffect(() => {
    const tick = () => {
      remaining.current = Math.max(0, remaining.current - 1);
      setDisplay(remaining.current);
      onTick?.(remaining.current);
      if (remaining.current <= 0) {
        clearInterval(tickRef.current);
        onExpire?.();
      }
    };
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [timerKey, onExpire, onTick]); // re-creates when timerKey changes

  const h = Math.floor(display / 3600);
  const m = Math.floor((display % 3600) / 60);
  const s = display % 60;
  const cls = display < 300 ? 'danger' : display < 600 ? 'warning' : '';
  return (
    <div className={`timer ${cls}`}>
      {h > 0 && `${String(h).padStart(2,'0')}:`}
      {String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}
      <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'var(--font-main)', marginTop:4 }}>TIME LEFT</div>
    </div>
  );
}

// ─── Fullscreen Warning ───────────────────────────────────────────────────────
function FullscreenWarning({ onReEnter }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.93)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--bg-card)', border:'2px solid var(--accent-danger)', borderRadius:'var(--radius-lg)', padding:40, maxWidth:460, textAlign:'center' }}>
        <div style={{ fontSize:52, marginBottom:16 }}>⛔</div>
        <h2 style={{ fontSize:22, fontWeight:700, color:'var(--accent-danger)', marginBottom:12 }}>Fullscreen Exited!</h2>
        <p style={{ fontSize:14, color:'var(--text-secondary)', lineHeight:1.7, marginBottom:24 }}>
          A violation has been logged. This exam must run in fullscreen at all times.
        </p>
        <button className="btn btn-primary btn-lg" style={{ width:'100%', justifyContent:'center' }} onClick={onReEnter}>
          ⛶ Return to Fullscreen →
        </button>
      </div>
    </div>
  );
}

// ─── Fullscreen Entry Prompt (used on refresh — browser needs a user gesture) ──
function FullscreenEntryPrompt({ onEnter, fsTransitionRef }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,0.97)',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      <div style={{ background:'var(--bg-card)', border:'2px solid var(--accent-primary)', borderRadius:'var(--radius-lg)', padding:48, maxWidth:480, textAlign:'center' }}>
        <div style={{ fontSize:52, marginBottom:20 }}>⛶</div>
        <h2 style={{ fontSize:24, fontWeight:700, marginBottom:12 }}>Fullscreen Required</h2>
        <p style={{ fontSize:14, color:'var(--text-secondary)', lineHeight:1.7, marginBottom:28 }}>
          This exam must be taken in fullscreen mode.<br />
          Click the button below to enter fullscreen and continue your exam.
        </p>
        <button
          className="btn btn-primary btn-lg"
          style={{ width:'100%', justifyContent:'center', fontSize:16 }}
          onClick={() => {
            fsTransitionRef.current = true; // suppress beforeunload during entry
            document.documentElement.requestFullscreen?.()
              .then(() => { setTimeout(() => { fsTransitionRef.current = false; }, 300); onEnter(); })
              .catch(() => { fsTransitionRef.current = false; onEnter(); });
          }}>
          ⛶ Enter Fullscreen & Continue →
        </button>
        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:16 }}>
          You can also press F11 on your keyboard
        </div>
      </div>
    </div>
  );
}

// ─── Instructor notification banner ──────────────────────────────────────────
function InstructorBanner({ msg, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 10000);
    return () => clearTimeout(t);
  }, [onClose]);
  const isGood = msg.action !== 'terminated';
  return (
    <div style={{
      position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)',
      zIndex:9998, minWidth:360, maxWidth:520,
      background: isGood ? 'rgba(16,185,129,0.13)' : 'rgba(239,68,68,0.13)',
      border:`2px solid ${isGood?'var(--accent-success)':'var(--accent-danger)'}`,
      borderRadius:'var(--radius-lg)', padding:'16px 20px',
      boxShadow:`0 8px 32px ${isGood?'rgba(16,185,129,0.3)':'rgba(239,68,68,0.3)'}`,
      animation:'fadeInDown 0.4s ease',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:15, color:isGood?'var(--accent-success)':'var(--accent-danger)', marginBottom:5 }}>{msg.title}</div>
          <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.5 }}>{msg.message}</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:18, flexShrink:0 }}>✕</button>
      </div>
    </div>
  );
}

// ─── ExamPage ─────────────────────────────────────────────────────────────────
export default function ExamPage() {
  const { examId }  = useParams();
  const location    = useLocation();
  const navigate    = useNavigate();
  const { user }    = useAuth();  // ← fix: call hook inside component

  // #8: Get attemptId from location.state OR sessionStorage (survives refresh)
  const attemptIdFromState = location.state?.attemptId;
  const attemptIdFromSS    = (() => {
    try {
      // Find any saved exam state for this examId
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith('exam_state_')) {
          const s = JSON.parse(sessionStorage.getItem(key));
          if (s?.examId === examId) return key.replace('exam_state_', '');
        }
      }
    } catch { /* silent */ }
    return null;
  })();
  const attemptId = attemptIdFromState || attemptIdFromSS;

  const [exam,             setExam]             = useState(null);
  const [questions,        setQuestions]        = useState([]);
  const [currentIdx,       setCurrentIdx]       = useState(0);
  const [answers,          setAnswers]          = useState({});
  const [marked,           setMarked]           = useState({});
  const [loading,          setLoading]          = useState(true);
  const [submitting,       setSubmitting]       = useState(false);
  const [showConfirm,      setShowConfirm]      = useState(false);
  const [examReady,        setExamReady]        = useState(false);
  const [proctoringWarning,setProctoringWarning]= useState(null);
  const [showFsWarning,    setShowFsWarning]    = useState(false);
  const [timeAdjustment,   setTimeAdjustment]   = useState(0);
  const [elapsedSeconds,   setElapsedSeconds]   = useState(0);
  const [initialSeconds,   setInitialSeconds]   = useState(0);
  const [timerKey,         setTimerKey]         = useState(0);
  const [instrNotif,       setInstrNotif]       = useState(null);
  const [showFsEntry,      setShowFsEntry]      = useState(false);
  const [instrMessages,    setInstrMessages]    = useState([]);
  const [proctorInfo,      setProctorInfo]      = useState(null);
  // Floating settings panel
  const [serverWarnings,   setServerWarnings]   = useState(0); // restored from server on load
  const [showFloatSettings,setShowFloatSettings]= useState(false);
  const [fontSize,         setFontSize]         = useState(() => parseInt(localStorage.getItem('exam_font_size') || '15'));
  const [darkMode,         setDarkMode]         = useState(() => document.documentElement.getAttribute('data-theme') !== 'light');

  const startTimeRef  = useRef(Date.now());
  const toastShownRef = useRef(false);
  const warnTimerRef  = useRef(null);
  const elapsedRef    = useRef(null);
  const heartbeatRef  = useRef(null);
  const saveStateRef  = useRef(null); // #7 periodic save

  // ── #7 & #8: Restore state from sessionStorage on mount ──────────────────
  // IMPORTANT: Load once at mount, adjust remaining time accounting for elapsed
  const savedState = useRef(null);
  if (savedState.current === null && attemptId) {
    savedState.current = loadExamState(attemptId) || undefined;
  }

  // ── #4 #5: Handle instructor actions ─────────────────────────────────────
  const handleInstructorAction = useCallback((msg) => {
    setInstrNotif(msg);
    if (msg.action === 'resumed' && msg.remaining_seconds) {
      remainingRef.current = msg.remaining_seconds;
      setInitialSeconds(msg.remaining_seconds);
      setTimerKey(k => k + 1);
      // DO NOT reset startTimeRef — elapsed time must continue from original start
      // startTimeRef stays as-is so submit-lock calculates correctly
      if (msg.existing_answers && Object.keys(msg.existing_answers).length > 0) {
        setAnswers(prev => ({ ...msg.existing_answers, ...prev }));
        answersRef.current = { ...msg.existing_answers, ...answersRef.current };
        toast.success(`✅ Exam resumed — ${Object.keys(msg.existing_answers).length} previous answers restored`, { toastId: 'resumed' });
      } else {
        toast.success('✅ Exam resumed!', { toastId: 'resumed' });
      }
    }
    if (msg.action === 'time_adjusted' && msg.adjustment_seconds) {
      setTimeAdjustment(prev => prev + msg.adjustment_seconds);
      const mins = Math.abs(Math.round(msg.adjustment_seconds / 60));
      toast[msg.adjustment_seconds > 0 ? 'success' : 'warning'](
        `⏱ ${msg.adjustment_seconds > 0 ? '+' : '−'}${mins} min by instructor`, { toastId:'time-adj' }
      );
    }
    if (msg.action === 'terminated') {
      clearExamState(attemptId);
      toast.error('⛔ Exam terminated by instructor.', { autoClose: false, toastId:'terminated' });
      setTimeout(() => navigate('/dashboard'), 5000);
    }
  }, [attemptId, navigate]);

  // #5: Handle proctor messages — separate handler, NOT inside action handler
  const handleInstructorMessage = useCallback((msg) => {
    setInstrMessages(prev => [msg, ...prev].slice(0, 20));
    // Also show a toast so student notices
    toast.info(`💬 ${msg.from}: ${msg.text.slice(0, 60)}${msg.text.length > 60 ? '...' : ''}`, {
      toastId: `msg-${msg.timestamp}`, autoClose: 8000,
    });
  }, []);

  // Fullscreen handlers
  const handleFullscreenExit = useCallback(() => setShowFsWarning(true), []);
  const handleReEnterFullscreen = useCallback(() => {
    document.documentElement.requestFullscreen?.()
      .then(() => setShowFsWarning(false))
      .catch(() => toast.error('Press F11 to re-enter fullscreen'));
  }, []);

  const handleTerminate = useCallback(() => {
    clearExamState(attemptId);
    activeAttemptRef.current = null; // disable page-leave protection
    toast.error('Exam terminated due to violations.', { autoClose: false, toastId:'terminated' });
    setTimeout(() => navigate('/dashboard'), 4000);
  }, [attemptId, navigate]);

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const { wsRef, sendHeartbeat, sendActivity, sendSubmit, sendCameraFrame, setOnAction, setOnMessage, setOnCameraReq, setVideoEl, captureViolationSnapshot } = useExamWebSocket({
    attemptId, examId, enabled: !!attemptId && !!examId,
  });

  // Register callbacks via refs — avoids WS reconnect on every render
  useEffect(() => { setOnAction(handleInstructorAction); }, [handleInstructorAction, setOnAction]);
  useEffect(() => { setOnMessage(handleInstructorMessage); }, [handleInstructorMessage, setOnMessage]);
  useEffect(() => {
    // #3 Camera frame request — capture from proctor video feed
    setOnCameraReq(() => () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;
      try {
        const cv = document.createElement('canvas');
        // Small resolution for fast transmission — 240x180 is plenty for identity verification
        cv.width  = 240;
        cv.height = 180;
        cv.getContext('2d').drawImage(video, 0, 0, 240, 180);
        sendCameraFrame(cv.toDataURL('image/jpeg', 0.5)); // 50% quality, ~8-15KB per frame
      } catch (err) { console.warn('[Camera] Frame capture failed:', err.message); }
    });
  }, [setOnCameraReq, sendCameraFrame]);

  // ── Proctoring ────────────────────────────────────────────────────────────
  const { videoRef, canvasRef, overlayRef, warnings, trustScore, events, cameraReady, proctorStatus, tfStatus } =
    useProctoring({
      attemptId,
      maxWarnings:         exam?.max_warnings || 3,
      enabled:             examReady && (!!exam?.proctoring_enabled || !!exam?.require_fullscreen),
      onTerminate:         handleTerminate,
      onFullscreenExit:    exam?.require_fullscreen !== false ? handleFullscreenExit : undefined,
      requireFullscreen:   exam?.require_fullscreen !== false,
      wsRef,
      captureSnapshot:     captureViolationSnapshot,
      // FIX 1: disabled violations must be respected on frontend too — these event types
      // still fire (and POST to backend for logging) but do NOT increment the local
      // warning counter or contribute to the auto-termination threshold.
      disabledViolations:  exam?.disabled_violations || [],
      // FIX 2: restore violation count from server after refresh
      initialWarnings:     serverWarnings,
    });

  // Register video element with WS hook so it can push frames automatically
  // videoRef comes from useProctoring (the actual <video> DOM element)
  // FIX: this previously had NO dependency array, meaning it re-ran on
  // EVERY render (including every 1-second timer tick) for the entire exam
  // duration — needless repeated work. It only actually needs to run once,
  // when the camera becomes ready (videoRef.current is guaranteed populated by then).
  useEffect(() => {
    if (videoRef?.current) setVideoEl(videoRef.current);
  }, [cameraReady]);

  // Show violation banner
  useEffect(() => {
    if (!events.length) return;
    setProctoringWarning(events[events.length - 1]);
    clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(() => setProctoringWarning(null), 5000);
  }, [events]);

  // ── Font size persistence ────────────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem('exam_font_size', fontSize);
  }, [fontSize]);

  // ── Page-leave protection ─────────────────────────────────────────────────
  const activeAttemptRef   = useRef(null); // set when exam ready, cleared on submit
  const fsTransitionRef    = useRef(false); // true during fullscreen enter/exit — suppress dialog

  useEffect(() => {
    // Suppress dialog during fullscreen transitions
    const onFsChange = () => {
      fsTransitionRef.current = true;
      // Reset after transition settles (300ms is enough)
      setTimeout(() => { fsTransitionRef.current = false; }, 300);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);

    // Show warning dialog only when exam is active AND not in a fullscreen transition
    const beforeUnloadHandler = (e) => {
      if (!activeAttemptRef.current) return;
      if (fsTransitionRef.current) return; // fullscreen change — not a real navigation
      e.preventDefault();
      const msg = '⚠ Refreshing will PERMANENTLY TERMINATE your exam!';
      e.returnValue = msg;
      return msg;
    };

    // Send beacon only on actual page unload
    const unloadHandler = () => {
      if (fsTransitionRef.current) return; // fullscreen change — ignore
      const aid = activeAttemptRef.current;
      const token = sessionStorage.getItem('token');
      if (!aid || !token) return;
      const blob = new Blob([JSON.stringify({ token })], { type: 'application/json' });
      navigator.sendBeacon(`/api/attempts/${aid}/terminate-refresh`, blob);
      try { clearExamState(aid); } catch {}
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    window.addEventListener('unload', unloadHandler);

    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('unload', unloadHandler);
    };
  }, []); // run once — uses refs, no deps needed

  // ── Load exam — fetch data and restore state ──────────────────────────────
  useEffect(() => {
    if (!attemptId) { navigate('/dashboard'); return; }
    Promise.all([
      axios.get(`/api/exams/${examId}/take`),
      axios.get(`/api/attempts/${attemptId}/state`).catch(() => null),
      axios.get(`/api/attempts/${attemptId}/proctor/messages`).catch(() => ({ data: [] })),
    ]).then(([examRes, stateRes, msgsRes]) => {
      const examData    = examRes.data;
      const serverState = stateRes?.data || null;
      const qs          = examData.questions || [];
      const ss          = savedState.current;

      setExam(examData);

      // ── Determine remaining seconds (DB is the authority) ────────────────
      let remaining;
      if (serverState && serverState.remaining_seconds > 0) {
        // Account for network round-trip delay (~100ms)
        const networkDelay = stateRes ? 0 : 0;
        remaining = Math.max(1, serverState.remaining_seconds - networkDelay);
      } else if (ss?.remainingSeconds > 0) {
        // sessionStorage fallback (already adjusted for elapsed in loadExamState)
        remaining = ss.remainingSeconds;
      } else {
        // Fresh start — full duration
        remaining = (location.state?.remainingSeconds) || (examData.duration_minutes * 60);
      }
      remainingRef.current = remaining;
      setInitialSeconds(remaining);    // set BEFORE setExamReady
      setTimerKey(k => k + 1);        // force Timer to reset with new value

      // ── Restore questions ────────────────────────────────────────────────
      if (ss?.questionOrder && ss.questionOrder.length === qs.length) {
        const ordered = ss.questionOrder.map(id => qs.find(q => q.id === id)).filter(Boolean);
        setQuestions(ordered.length === qs.length ? ordered : qs);
      } else {
        setQuestions(qs);
      }

      // ── Restore answers ─────────────────────────────────────────────────
      // Merge: server (DB) answers as base + sessionStorage on top (local wins conflicts)
      const serverAnswers = (serverState?.answers && Object.keys(serverState.answers).length > 0)
        ? serverState.answers : {};
      const localAnswers  = (ss?.answers && Object.keys(ss.answers).length > 0)
        ? ss.answers : {};
      const mergedAnswers = { ...serverAnswers, ...localAnswers };

      if (Object.keys(mergedAnswers).length > 0) {
        setAnswers(mergedAnswers);
        answersRef.current = mergedAnswers; // sync ref immediately
      }
      if (ss?.marked)      setMarked(ss.marked);
      if (ss?.currentIdx)  setCurrentIdx(ss.currentIdx);
      if (ss?.timeAdjustment) setTimeAdjustment(ss.timeAdjustment);

      // ── FIX 2: violations notification + count restored ──────────────────
      if (serverState?.total_warnings > 0) {
        setServerWarnings(serverState.total_warnings);
        toast.warning(`⚠ ${serverState.total_warnings} previous violation${serverState.total_warnings>1?'s':''} on record`, { autoClose: 5000 });
      }

      // ── Proctor messages ─────────────────────────────────────────────────
      if (msgsRes?.data?.length) {
        setInstrMessages(msgsRes.data.map(m => ({
          from: m.from_name, fromId: m.from_user,
          text: m.message, timestamp: new Date(m.sent_at).getTime(), id: m.id,
        })));
      }

      // ── Fullscreen + proctoring ──────────────────────────────────────────
      if (examData.require_fullscreen !== false && !document.fullscreenElement) {
        setShowFsEntry(true);
      }
      if (examData.proctoring_enabled && !toastShownRef.current) {
        toastShownRef.current = true;
        toast.info('🔒 AI Proctoring is active.', { autoClose: 4000, toastId:'proctor-notice' });
      }

      // ── Set startTimeRef from server's started_at (not Date.now()) ──────────
      if (serverState?.started_at) {
        startTimeRef.current = new Date(serverState.started_at).getTime();
      }

      // ── Start everything AFTER all state is set ────────────────────────────
      setExamReady(true);
      activeAttemptRef.current = attemptId; // activate page-leave protection

    }).catch((err) => {
      const msg = err?.response?.data?.error || 'Failed to load exam. Please try again.';
      toast.error(msg, { autoClose: 6000, toastId: 'exam-load-error' });
      console.error('Exam load error:', err?.response?.data || err.message);
      setTimeout(() => navigate('/dashboard'), 3000);
    }).finally(() => setLoading(false));

    // cleanup is handled by the beforeunload/pagehide useEffect return above
  }, [examId, attemptId]);

  // ── #7: Save exam state to sessionStorage every 5 seconds ─────────────────
  useEffect(() => {
    if (!examReady || !attemptId) return;
    saveStateRef.current = setInterval(() => {
      saveExamState(attemptId, {
        examId,
        remainingSeconds: remainingRef.current,
        answers: answersRef.current,  // use ref — always latest without re-running interval
        marked,
        currentIdx,
        questionOrder: questions.map(q => q.id),
      });
    }, 5000);
    return () => clearInterval(saveStateRef.current);
  }, [examReady, attemptId, examId, marked, currentIdx, questions]);

  // ── Elapsed timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!examReady) return;
    elapsedRef.current = setInterval(() =>
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(elapsedRef.current);
  }, [examReady]);

  // ── Fix 4: Server heartbeat — saves remaining time AND answers to DB every 8s ─
  const remainingRef  = useRef(initialSeconds);
  const answersRef    = useRef({});
  // Keep answersRef in sync with answers state
  useEffect(() => { answersRef.current = answers; }, [answers]);

  useEffect(() => {
    if (!examReady || !attemptId) return;
    const hb = setInterval(() => {
      const remaining = remainingRef.current;
      if (remaining > 0) {
        axios.post(`/api/attempts/${attemptId}/heartbeat`, {
          remaining_seconds: remaining,
          answers: answersRef.current,  // save answers to DB every heartbeat
        }).then(r => {
          if (r.data?.terminated) {
            clearExamState(attemptId);
            window.location.href = '/dashboard';
          }
        }).catch(() => {});
      }
    }, 8000);
    return () => clearInterval(hb);
  }, [examReady, attemptId]);

  // ── WS heartbeat ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!examReady) return;
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat({ answeredCount: Object.keys(answers).length, totalQuestions: questions.length, trustScore, violations: warnings });
    }, 10000);
    return () => clearInterval(heartbeatRef.current);
  }, [examReady, answers, questions.length, trustScore, warnings, sendHeartbeat]);

  // ── Timer tick callback — save remaining ──────────────────────────────────
  const handleTimerTick = useCallback((remainingSecs) => {
    if (!attemptId) return;
    // Fix 4: Keep server heartbeat ref in sync
    remainingRef.current = remainingSecs;
    // Lightweight update — only update remaining seconds in SS without triggering re-render
    try {
      const raw = sessionStorage.getItem(SS_KEY(attemptId));
      if (raw) {
        const s = JSON.parse(raw);
        s.remainingSeconds = remainingSecs;
        s.savedAt = Date.now();
        sessionStorage.setItem(SS_KEY(attemptId), JSON.stringify(s));
      }
    } catch { /* silent */ }
  }, [attemptId]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setShowConfirm(false);
    activeAttemptRef.current = null; // disable page-leave protection before navigating away
    try {
      const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, initialSeconds - timeSpent + timeAdjustment);
      const res = await axios.post(`/api/attempts/${attemptId}/submit`, {
        answers, time_spent_seconds: timeSpent, remaining_seconds: remaining,
      });
      clearExamState(attemptId);
      sendSubmit({ score: res.data.score });
      toast.success('Exam submitted!', { toastId:'submitted' });
      navigate(`/result/${attemptId}`, { state: { result: res.data } });
    } catch {
      activeAttemptRef.current = attemptId; // re-enable if submit fails
      toast.error('Failed to submit. Try again.');
      setSubmitting(false);
    }
  }, [submitting, attemptId, answers, navigate, initialSeconds, timeAdjustment, sendSubmit]);

  const handleTimeExpire = useCallback(() => {
    toast.warning('⏰ Time is up!', { toastId:'time-up' });
    handleSubmit();
  }, [handleSubmit]);

  const goToQuestion = (idx) => {
    setCurrentIdx(idx);
    sendActivity({ currentQuestion: idx + 1, answeredCount: Object.keys(answers).length });
  };

  const toggleMark = (qId) => setMarked(prev => ({ ...prev, [qId]: !prev[qId] }));

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!exam)   return null;

  const q             = questions[currentIdx];
  const answered      = Object.keys(answers).length;
  const options       = q?.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : [];
  const letters       = ['A','B','C','D','E'];
  const getTrustColor = s => s >= 75 ? 'var(--accent-success)' : s >= 40 ? 'var(--accent-warning)' : 'var(--accent-danger)';
  const minSubmitSecs = (exam.min_submit_minutes || 0) * 60;
  const submitLocked  = elapsedSeconds < minSubmitSecs;
  const lockRemain    = Math.max(0, minSubmitSecs - elapsedSeconds);
  const markedCount   = Object.keys(marked).filter(k => marked[k]).length;

  return (
    <div className="exam-layout" style={{ userSelect:'none' }}>

      {/* Fullscreen entry prompt on page refresh (#2) */}
      {showFsEntry && exam?.require_fullscreen !== false && (
        <FullscreenEntryPrompt onEnter={() => setShowFsEntry(false)} fsTransitionRef={fsTransitionRef} />
      )}

      {showFsWarning && exam?.require_fullscreen !== false && (
        <FullscreenWarning onReEnter={handleReEnterFullscreen} />
      )}

      {instrNotif && (
        <InstructorBanner msg={instrNotif} onClose={() => setInstrNotif(null)} />
      )}

      {/* ── Left sidebar ─────────────────────────────────────────────────── */}
      <div className="exam-sidebar-left">
        <div style={{ padding:'16px', borderBottom:'1px solid var(--border)' }}>
          {/* Org logo + name — larger, clearer */}
          {exam.org_logo_url && (
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, paddingBottom:12, borderBottom:'1px solid var(--border)' }}>
              <img src={exam.org_logo_url} alt="org logo"
                style={{ height:48, width:48, borderRadius:8, objectFit:'contain', background:'#fff', border:'1px solid var(--border)', padding:3, flexShrink:0 }} />
              <span style={{ fontSize:14, color:'var(--text-primary)', fontWeight:700, flex:1, lineHeight:1.3 }}>{exam.org_name}</span>
            </div>
          )}
          <div style={{ fontSize:11, fontWeight:600, color:'var(--accent-primary)', marginBottom:4 }}>⬡ ExamAI</div>
          <div style={{ fontSize:14, fontWeight:600, lineHeight:1.3, marginBottom:2 }}>{exam.title}</div>
          <div style={{ fontSize:12, color:'var(--text-muted)' }}>{exam.total_marks} marks</div>

          {/* Student identity card — larger photo + name */}
          {exam.show_student_photos !== false && (
            <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:10, border:'1px solid var(--border)' }}>
              {exam.student_photo_url ? (
                <img src={exam.student_photo_url} alt="your photo"
                  style={{ width:52, height:52, borderRadius:26, objectFit:'cover', border:'2px solid var(--accent-primary)', flexShrink:0 }} />
              ) : (
                <div style={{ width:52, height:52, borderRadius:26, background:'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:20, flexShrink:0 }}>
                  {(user?.full_name || exam.student_name || 'S')?.[0]?.toUpperCase()}
                </div>
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:14, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {user?.full_name || exam.student_name || 'Student'}
                </div>
                <div style={{ color:'var(--accent-success)', fontSize:11, marginTop:2 }}>✓ Identity Verified</div>
              </div>
            </div>
          )}
        </div>

        <Timer
          key={timerKey}
          timerKey={timerKey}
          initialSeconds={initialSeconds}
          onExpire={handleTimeExpire}
          onTick={handleTimerTick}
        />

        {/* Legend */}
        <div style={{ padding:'8px 16px', borderBottom:'1px solid var(--border)', display:'flex', flexWrap:'wrap', gap:8 }}>
          {[
            { bg:'rgba(16,185,129,0.25)', border:'rgba(16,185,129,0.5)', label:'Answered' },
            { bg:'rgba(245,158,11,0.2)',  border:'rgba(245,158,11,0.5)', label:'Review' },
            { bg:'var(--bg-elevated)',    border:'var(--border)',         label:'Not done' },
          ].map(l => (
            <div key={l.label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'var(--text-muted)' }}>
              <span style={{ width:10, height:10, borderRadius:2, background:l.bg, border:`1px solid ${l.border}`, display:'inline-block' }} />
              {l.label}
            </div>
          ))}
        </div>

        {/* Question grid */}
        <div className="question-nav">
          <div className="question-nav-title">{answered}/{questions.length} answered · {markedCount} review</div>
          <div className="q-nav-grid">
            {questions.map((qItem, i) => {
              const isAns  = answers[qItem.id] !== undefined;
              const isMark = marked[qItem.id];
              const isAct  = i === currentIdx;
              return (
                <button key={i} onClick={() => goToQuestion(i)}
                  style={{
                    width:32, height:32, borderRadius:6, fontSize:12, fontWeight:600,
                    cursor:'pointer', border:'none', transition:'all 0.15s', position:'relative',
                    background: isAct?'rgba(79,142,247,0.3)':isMark?'rgba(245,158,11,0.2)':isAns?'rgba(16,185,129,0.2)':'var(--bg-elevated)',
                    outline: isAct?'2px solid var(--accent-primary)':isMark?'2px solid rgba(245,158,11,0.6)':isAns?'2px solid rgba(16,185,129,0.4)':'1px solid var(--border)',
                    color: isAct?'var(--accent-primary)':isMark?'var(--accent-warning)':isAns?'var(--accent-success)':'var(--text-secondary)',
                  }}>
                  {i + 1}
                  {isMark && <span style={{ position:'absolute', top:-3, right:-3, width:7, height:7, borderRadius:'50%', background:'var(--accent-warning)', border:'1px solid var(--bg-primary)' }} />}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ padding:'8px 16px', borderTop:'1px solid var(--border)', fontSize:12, color:'var(--text-secondary)', marginBottom:4 }}>
          ✅ {answered} · 🟡 {markedCount} · ⬜ {questions.length - answered}
        </div>
        <div style={{ padding:'0 16px 16px' }}>
          {submitLocked && (
            <div style={{ padding:'8px 12px', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8, marginBottom:10, fontSize:12, color:'var(--accent-warning)', textAlign:'center' }}>
              🔒 Submit unlocks in <strong style={{ fontFamily:'var(--font-mono)' }}>
                {String(Math.floor(lockRemain/60)).padStart(2,'0')}:{String(lockRemain%60).padStart(2,'0')}
              </strong>
            </div>
          )}
          <button className="btn btn-primary w-full" onClick={() => setShowConfirm(true)} disabled={submitting || submitLocked}>
            {submitting ? 'Submitting...' : submitLocked ? '🔒 Locked' : 'Submit Exam'}
          </button>
        </div>
      </div>

      {/* ── Floating ⚙ Settings — always visible bottom-right ─────────────── */}
      {exam?.allow_floating_settings !== false && (
        <>
          <button onClick={() => setShowFloatSettings(s => !s)}
            style={{ position:'fixed', bottom:24, right:24, zIndex:1000, width:46, height:46, borderRadius:23,
              background:'var(--accent-primary)', color:'#fff', border:'none', cursor:'pointer', fontSize:22,
              boxShadow:'0 4px 16px rgba(0,0,0,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}
            title="Display Settings">
            {showFloatSettings ? '✕' : '⚙'}
          </button>
          {showFloatSettings && (
            <div style={{ position:'fixed', bottom:78, right:24, zIndex:1000, width:230,
              background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14,
              padding:'16px', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:14 }}>⚙ Display Settings</div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>Theme</div>
                <div style={{ display:'flex', gap:6 }}>
                  {[['☀️ Light', false], ['🌙 Dark', true]].map(([lbl, dark]) => (
                    <button key={lbl} onClick={() => {
                      setDarkMode(dark);
                      document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
                      localStorage.setItem('theme', dark ? 'dark' : 'light');
                    }} style={{ flex:1, padding:'7px 0', borderRadius:8, border:'1px solid var(--border)',
                      background: darkMode === dark ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                      color: darkMode === dark ? '#fff' : 'var(--text-primary)', cursor:'pointer', fontSize:12, fontWeight: darkMode === dark ? 700 : 400 }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>Font Size</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={() => setFontSize(s => Math.max(12, s - 1))} style={{ width:30, height:30, borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-elevated)', cursor:'pointer', fontSize:16, color:'var(--text-primary)' }}>−</button>
                  <span style={{ flex:1, textAlign:'center', fontWeight:700, fontSize:15 }}>{fontSize}px</span>
                  <button onClick={() => setFontSize(s => Math.min(22, s + 1))} style={{ width:30, height:30, borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-elevated)', cursor:'pointer', fontSize:16, color:'var(--text-primary)' }}>+</button>
                </div>
                <input type="range" min={12} max={22} value={fontSize} onChange={e => setFontSize(+e.target.value)}
                  style={{ width:'100%', marginTop:8, accentColor:'var(--accent-primary)' }} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="exam-main">
        {proctoringWarning && (
          <div className={`alert alert-${['critical','high'].includes(proctoringWarning.severity)?'danger':'warning'}`}
            style={{ position:'sticky', top:0, zIndex:10, marginBottom:20, animation:'fadeInDown 0.3s ease' }}>
            ⚠️ <strong>AI Alert:</strong> {proctoringWarning.description}
          </div>
        )}

        {q && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:12, color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>
                Question {currentIdx + 1} of {questions.length}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <span className="badge badge-blue">{q.marks} mark{q.marks!==1?'s':''}</span>
                <button onClick={() => toggleMark(q.id)} style={{
                  padding:'4px 12px', fontSize:12, fontWeight:600, borderRadius:6, cursor:'pointer', transition:'all 0.2s',
                  border: marked[q.id]?'1px solid var(--accent-warning)':'1px solid var(--border)',
                  background: marked[q.id]?'rgba(245,158,11,0.15)':'var(--bg-elevated)',
                  color: marked[q.id]?'var(--accent-warning)':'var(--text-secondary)',
                }}>
                  {marked[q.id] ? '🟡 Marked' : '○ Mark for Review'}
                </button>
              </div>
            </div>

            <div className="card" style={{ marginBottom:24, padding:28 }}>
              <RichContent html={q.question_text} style={{ fontSize: fontSize, fontWeight:500 }} />
            </div>

            {(q.question_type === 'mcq' || q.question_type === 'true_false') && (
              <div>
                {(q.question_type === 'true_false' ? ['True','False'] : options).map((opt,i) => {
                  // Parse option images stored in explanation JSON
                  let optImgs = [];
                  try {
                    const parsed = JSON.parse(q.explanation || '{}');
                    optImgs = parsed.option_images || [];
                  } catch { optImgs = []; }
                  const optImg = optImgs[i];
                  return (
                    <div key={i} className={`option-card ${answers[q.id]===opt?'selected':''}`}
                      onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}>
                      <div className="option-letter">{letters[i]}</div>
                      <div style={{ flex:1 }}>
                        {opt && <span style={{ fontSize:15 }}>{opt}</span>}
                        {/* Show option image if present (#1 fix) */}
                        {optImg && (
                          <img src={optImg} alt={`Option ${letters[i]}`}
                            style={{ display:'block', maxWidth:'100%', maxHeight:140, borderRadius:6, marginTop: opt ? 8 : 0, objectFit:'contain' }} />
                        )}
                      </div>
                      {answers[q.id]===opt && <span style={{ marginLeft:'auto', color:'var(--accent-success)', flexShrink:0 }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {(q.question_type === 'short_answer' || q.question_type === 'essay') && (
              <textarea className="form-input form-textarea"
                style={{ minHeight:q.question_type==='essay'?240:100, fontSize:15 }}
                placeholder={q.question_type==='essay'?'Write your answer here...':'Enter your answer...'}
                value={answers[q.id]||''}
                onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))} />
            )}

            <div style={{ display:'flex', justifyContent:'space-between', marginTop:32 }}>
              <button className="btn btn-secondary" onClick={() => goToQuestion(Math.max(0,currentIdx-1))} disabled={currentIdx===0}>← Previous</button>
              <div style={{ display:'flex', gap:8 }}>
                {answers[q.id] !== undefined && (
                  <button className="btn btn-secondary btn-sm"
                    style={{ color:'var(--accent-danger)', borderColor:'rgba(239,68,68,0.3)' }}
                    onClick={() => setAnswers(prev => { const n={...prev}; delete n[q.id]; return n; })}>Clear</button>
                )}
                {currentIdx < questions.length-1
                  ? <button className="btn btn-primary" onClick={() => goToQuestion(currentIdx+1)}>Next →</button>
                  : <button className="btn btn-success" onClick={() => setShowConfirm(true)} disabled={submitLocked}>Review & Submit ✓</button>
                }
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Right sidebar — proctoring + proctor info + messages ─────────── */}
      {exam.proctoring_enabled && (
        <div className="exam-sidebar-right">
          <div style={{ padding:14, borderBottom:'1px solid var(--border)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px' }}>🔒 AI Proctoring</span>
              <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, fontWeight:700,
                background:tfStatus==='ready'?'rgba(16,185,129,0.15)':'rgba(245,158,11,0.15)',
                color:tfStatus==='ready'?'var(--accent-success)':'var(--accent-warning)' }}>
                {tfStatus==='ready'?'TF READY':'PIXEL AI'}
              </span>
            </div>

            {/* Camera with overlay */}
            <div style={{ position:'relative', width:'100%', aspectRatio:'4/3', borderRadius:8, overflow:'hidden', background:'#000', border:`2px solid ${cameraReady?'var(--accent-success)':'var(--border)'}` }}>
              <video ref={videoRef} autoPlay muted playsInline style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              <canvas ref={overlayRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} />
              <canvas ref={canvasRef} style={{ display:'none' }} />
              {!cameraReady && (
                <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#111', gap:8 }}>
                  <div style={{ fontSize:24 }}>📷</div>
                  <div style={{ fontSize:11, color:'var(--text-secondary)' }}>Starting camera...</div>
                </div>
              )}
            </div>

            {/* Status */}
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:8 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:proctorStatus==='warning'?'var(--accent-warning)':'var(--accent-success)', animation:'blink 2s infinite' }} />
              <span style={{ fontSize:11, color:'var(--text-secondary)' }}>
                {proctorStatus==='active'?'Monitoring active':proctorStatus==='warning'?'Violation detected!':'Initializing...'}
              </span>
            </div>

            {/* Trust score */}
            <div style={{ marginTop:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                <span style={{ color:'var(--text-muted)' }}>Trust Score</span>
                <span style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:getTrustColor(trustScore) }}>{Math.round(trustScore)}%</span>
              </div>
              <div style={{ height:5, background:'var(--bg-elevated)', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${trustScore}%`, background:getTrustColor(trustScore), transition:'width 0.5s', borderRadius:3 }} />
              </div>
            </div>

            {/* #1 Violations remaining */}
            <div style={{ marginTop:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                <span style={{ color:'var(--text-muted)' }}>Violations</span>
                <span style={{ fontFamily:'var(--font-mono)', color:warnings>0?'var(--accent-danger)':'var(--text-secondary)', fontWeight:600 }}>
                  {warnings}/{exam.max_warnings||3}
                </span>
              </div>
              <div style={{ height:4, background:'var(--bg-elevated)', borderRadius:2, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${Math.min(100,(warnings/(exam.max_warnings||3))*100)}%`, background:warnings>=(exam.max_warnings||3)?'var(--accent-danger)':'var(--accent-warning)', transition:'width 0.5s', borderRadius:2 }} />
              </div>
              <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>
                {Math.max(0,(exam.max_warnings||3)-warnings)} more before auto-termination
              </div>
            </div>
          </div>

          {/* Event log */}
          <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>Events</div>
            {events.length === 0 ? (
              <div style={{ fontSize:11, color:'var(--accent-success)', textAlign:'center', paddingTop:8 }}>✓ No violations</div>
            ) : [...events].reverse().slice(0,8).map((ev,i) => (
              <div key={i} style={{ borderLeft:`3px solid ${ev.severity==='critical'?'#ff4444':ev.severity==='high'?'var(--accent-danger)':'var(--accent-warning)'}`, paddingLeft:8, marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--text-primary)' }}>{ev.type.replace(/_/g,' ').toUpperCase()}</div>
                <div style={{ fontSize:10, color:'var(--text-muted)' }}>{new Date(ev.time).toLocaleTimeString()}</div>
                {ev.snapshot && (
                  <img src={ev.snapshot} alt="violation"
                    style={{ width:'100%', borderRadius:4, marginTop:4, border:'1px solid var(--border)', cursor:'pointer' }}
                    onClick={() => window.open(ev.snapshot, '_blank')} />
                )}
              </div>
            ))}
          </div>

          {/* #4 Proctor info */}
          <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>Exam Supervisor</div>
            <div style={{ fontSize:13, fontWeight:600 }}>{exam.instructor_name || 'Instructor'}</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>AI Proctoring Active</div>
          </div>

          {/* #5 #11 Proctor messages — always visible */}
          <div style={{ padding:'10px 14px', flex:1, overflowY:'auto', background: instrMessages.length ? 'rgba(79,142,247,0.04)' : 'transparent' }}>
            <div style={{ fontSize:10, fontWeight:700, color: instrMessages.length ? 'var(--accent-primary)' : 'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:6 }}>
              📨 Proctor Messages {instrMessages.length > 0 && `(${instrMessages.length})`}
            </div>
            {instrMessages.length === 0 ? (
              <div style={{ fontSize:11, color:'var(--text-muted)', fontStyle:'italic' }}>No messages yet</div>
            ) : (
              instrMessages.map((msg,i) => (
                <div key={i} style={{ padding:'8px 10px', background:'rgba(79,142,247,0.1)', borderRadius:6, marginBottom:8, border:'1px solid rgba(79,142,247,0.2)' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--accent-primary)', marginBottom:3 }}>
                    {msg.from} <span style={{ fontWeight:400, fontSize:10, color:'var(--text-muted)' }}>· {new Date(msg.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <div style={{ fontSize:13, color:'var(--text-primary)', lineHeight:1.5 }}>{msg.text}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Submit modal */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:520 }}>
            <h3 className="modal-title">Submit Exam?</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
              {[
                { label:'Answered',  val:answered,                   color:'var(--accent-success)' },
                { label:'For Review',val:markedCount,                color:'var(--accent-warning)' },
                { label:'Not Done',  val:questions.length-answered,  color:'var(--text-secondary)' },
              ].map(s => (
                <div key={s.label} style={{ textAlign:'center', padding:'12px 8px', background:'var(--bg-elevated)', borderRadius:8 }}>
                  <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{s.val}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.label}</div>
                </div>
              ))}
            </div>
            {markedCount > 0 && (
              <div className="alert alert-warning" style={{ marginBottom:12 }}>
                ⚠️ {markedCount} question{markedCount>1?'s':''} still marked for review.
              </div>
            )}
            <div className="alert alert-warning">⚠️ Once submitted, answers cannot be changed.</div>
            <div style={{ display:'flex', gap:12, marginTop:20, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Yes, Submit →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
